<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { UserUpdateDocument, UserType } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue-next';

// --- Props for user details ---
const props = defineProps<{
  user: UserType;
  loading: boolean;
}>();

// --- Local state for user details ---
const userFormValues = ref<Partial<UserType>>({});

// Watch the incoming user details for updates => update local form
watch(
  () => props.user,
  (newVal) => {
    console.debug('User details updated:', newVal);
    userFormValues.value = {
      email: newVal?.email || '',
      givenName: newVal?.givenName || '',
      familyName: newVal?.familyName || '',
    };
  },
  { immediate: true }
);

// --- Emit "userUpdated" when personal details are updated ---
const emits = defineEmits(['userUpdated']);

// Loading and Error States
const loadingUpdatePersonal = ref(false);
const showUpdateSuccess = ref(false);
const errorUpdatePersonal = ref<string | null>(null);

// --- Mutation to updated user`s personal details ---
const {
  mutate: updateDetails,
  onDone: onUpdateDone,
  onError: onUpdateError,
} = useMutation(UserUpdateDocument);

// Handle the result of the mutation
onUpdateDone((res) => {
  if (res.data && res.data.userUpdate) {
    console.debug('User details updated:', res.data.userUpdate);
    emits('userUpdated');
    // Show success indicator and hider after 2 seconds
    loadingUpdatePersonal.value = false;
    showUpdateSuccess.value = true;
    setTimeout(() => {
      showUpdateSuccess.value = false;
    }, 1000);
  } else {
    console.error('User update failed:', res.data);
  }
});

// Handle errors in the mutation
onUpdateError((err) => {
  console.error('Error updating user details:', err);
  loadingUpdatePersonal.value = false;
  errorUpdatePersonal.value = err.message;
});

// Submit form with personal details
async function onSubmit() {
  // Reset error and submit the update request
  errorUpdatePersonal.value = null;
  loadingUpdatePersonal.value = true;

  updateDetails({
    givenName: userFormValues.value.givenName,
    familyName: userFormValues.value.familyName,
  });
}
</script>

<template>
  <div>
    <b-form @submit.prevent="onSubmit">
      <!-- 1. E-mail (read-only) -->
      <b-form-group
        id="input-group-email"
        label="Email address"
        label-for="input-email"
        description="E-mail address cannot be changed."
        class="mt-3"
      >
        <b-form-input
          disabled
          id="input-email"
          v-model="userFormValues.email"
          type="email"
          name="email"
          placeholder="Enter email"
          required
        ></b-form-input>
      </b-form-group>

      <!-- 2. Given Name -->
      <b-form-group
        id="input-group-given-name"
        label="Given Name"
        label-for="input-given-name"
        class="mt-3"
      >
        <b-form-input
          id="input-given-name"
          name="givenName"
          required
          v-model="userFormValues.givenName"
        ></b-form-input>
      </b-form-group>

      <!-- 3. Family Name -->
      <b-form-group
        id="input-group-family-name"
        label="Family Name"
        label-for="input-family-name"
        class="mt-3"
      >
        <b-form-input
          id="input-family-name"
          name="familyName"
          required
          v-model="userFormValues.familyName"
        ></b-form-input>
      </b-form-group>

      <!-- Save Changes Button -->
      <b-form-group class="mt-3">
        <b-button
          type="submit"
          variant="outline-dark"
          :disabled="loadingUpdatePersonal"
        >
          <span v-if="loadingUpdatePersonal">
            Saving Changes
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="showUpdateSuccess">
            Changes Saved
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Save Changes </span>
        </b-button>
      </b-form-group>

      <!-- Error Message -->
      <b-alert
        v-model="errorUpdatePersonal"
        variant="danger"
        dismissible
        class="mt-3"
        @dismissed="errorUpdatePersonal = null"
      >
        {{ errorUpdatePersonal }}
      </b-alert>
    </b-form>
  </div>
</template>
<style scoped></style>
